/**
 * Auth Redirect Path
 *
 * Shared helpers for safe auth continuation paths and login URLs.
 *
 * Primary responsibilities:
 * - Normalize and validate auth `next` values
 * - Prevent open redirects and auth-route loops
 * - Preserve safe `next` values on login retries
 *
 * Dependencies:
 * - None
 */

const AUTH_REDIRECT_BASE = 'https://auth.losi.local'
export const NEXUS_ONBOARDING_SIGNUP_SOURCE = 'enterprise_signup'
const BLOCKED_AUTH_PATHS = new Set([
  '/login',
  '/auth/callback',
  '/auth/confirm'
])
const ABSOLUTE_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/

function decodeAuthPath(value: string | null | undefined) {
  if (!value) {
    return null
  }

  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function getSafeAuthNextPath(
  value: string | null | undefined
): string | null {
  const decodedValue = decodeAuthPath(value)?.trim()

  if (!decodedValue || decodedValue.includes('\\')) {
    return null
  }

  if (decodedValue.startsWith('//')) {
    return null
  }

  const normalizedValue = decodedValue.startsWith('/')
    ? decodedValue
    : ABSOLUTE_SCHEME_PATTERN.test(decodedValue)
      ? null
      : `/${decodedValue}`

  if (!normalizedValue || normalizedValue.startsWith('//')) {
    return null
  }

  try {
    const url = new URL(normalizedValue, AUTH_REDIRECT_BASE)

    if (url.origin !== AUTH_REDIRECT_BASE) {
      return null
    }

    if (BLOCKED_AUTH_PATHS.has(url.pathname)) {
      return null
    }

    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

type BuildLoginPathOptions = {
  message?: string | null
  next?: string | null
  register?: boolean
  desktop?: string | null
}

export function buildLoginPath({
  message,
  next,
  register = false,
  desktop
}: BuildLoginPathOptions = {}) {
  const searchParams = new URLSearchParams()
  const safeNext = getSafeAuthNextPath(next)

  if (message) {
    searchParams.set('message', message)
  }

  if (safeNext) {
    searchParams.set('next', safeNext)
  }

  if (register) {
    searchParams.set('register', 'true')
  }

  if (desktop) {
    searchParams.set('desktop', desktop)
  }

  const queryString = searchParams.toString()

  return queryString ? `/login?${queryString}` : '/login'
}

type BuildAuthCallbackSearchParamsOptions = {
  next?: string | null
  popup?: boolean
  desktop?: string | null
  origin?: string | null
}

export function buildAuthCallbackSearchParams({
  next,
  popup = false,
  desktop,
  origin
}: BuildAuthCallbackSearchParamsOptions = {}) {
  const searchParams = new URLSearchParams()
  const safeNext = getSafeAuthNextPath(next)

  if (safeNext) {
    searchParams.set('next', safeNext)
  }

  if (popup) {
    searchParams.set('popup', 'true')
  }

  if (desktop) {
    searchParams.set('desktop', desktop)
  }

  if (origin) {
    searchParams.set('origin', origin)
  }

  return searchParams
}

export function getAuthRedirectTarget(
  next: string | null | undefined,
  fallbackPath: string
) {
  return getSafeAuthNextPath(next) || fallbackPath
}

export function isNexusOnboardingSignupPath(value: string | null | undefined) {
  const safePath = getSafeAuthNextPath(value)

  if (!safePath) {
    return false
  }

  try {
    const url = new URL(safePath, AUTH_REDIRECT_BASE)

    return (
      url.pathname === '/nexus/onboarding' &&
      url.searchParams.get('source') === NEXUS_ONBOARDING_SIGNUP_SOURCE
    )
  } catch {
    return false
  }
}
